import { getShapeById } from '@takeshape/schema';
import type { SagaIterator } from 'redux-saga';
import { all, call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { ensureDefaultRuntimeSchema } from '../branches/sagas/fetch-schema.js';
import type { BoundActionType } from '../common/utils/actions.js';
import { getBoundRoutineSaga } from '../common/utils/new-sagas.js';
import { showMessage } from '../messages/actions.js';
import {
  paginateBack,
  paginateForward,
  type SearchMeta,
  search,
  suggestTaxonomy,
  updateFilter,
  updateQuery,
  updateSort
} from './actions';
import { type SearchShapeParams, searchQuery, searchShapeQuery, taxonomySuggestQuery } from './api.js';
import { SEARCH_DELAY, SUGGEST_DELAY } from './constants.js';
import { getSearchState, hasRelatedTaxonomy } from './selectors.js';
import type { ContentSearchState, SearchResponse, TaxonomySuggestResponse } from './types.js';

export function* searchContentSaga(payload: unknown, meta: SearchMeta): SagaIterator<SearchResponse> {
  const searchState: ContentSearchState = yield select(getSearchState, meta);

  const { page, pageSize, filters, terms, sort, fields, shapeIds } = searchState;

  const projectSchema = yield call(ensureDefaultRuntimeSchema);

  const query: SearchShapeParams = {
    projectSchema,
    shapeIds,
    terms,
    sort,
    filters,
    fields,
    page,
    pageSize
  };

  const useListQuery =
    query.shapeIds.length === 1 && getShapeById(projectSchema, query.shapeIds[0])?.model?.type !== 'single';
  const res = yield call(useListQuery ? searchShapeQuery : searchQuery, query);
  return res.search;
}

export function* suggestTaxonomySaga(payload: unknown, meta: SearchMeta): SagaIterator<TaxonomySuggestResponse> {
  const searchState: ContentSearchState = yield select(getSearchState, meta);
  const params = {
    shapeIds: searchState.shapeIds,
    terms: searchState.terms
  };
  const res = yield call(taxonomySuggestQuery, params);
  return res.search;
}

const searchSaga = getBoundRoutineSaga(search, searchContentSaga);
const suggestSaga = getBoundRoutineSaga(suggestTaxonomy, suggestTaxonomySaga);

export function* searchOnPauseSaga({ meta }: BoundActionType<typeof updateQuery>): SagaIterator {
  yield delay(SEARCH_DELAY);
  yield put(search.requested(meta)({}));
}

export function* suggestOnPauseSaga({ meta }: BoundActionType<typeof updateQuery>): SagaIterator {
  if (yield select(hasRelatedTaxonomy, meta)) {
    yield delay(SUGGEST_DELAY);
    yield put(suggestTaxonomy.requested(meta)());
  }
}

export function* handleSearchRejected({ payload }: ActionType<typeof search.rejected>): SagaIterator {
  yield put(showMessage('error', payload));
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(String(updateQuery), searchOnPauseSaga),
    takeLatest(String(updateQuery), suggestOnPauseSaga),
    takeEvery(
      [String(updateSort), search.REQUESTED, String(paginateBack), String(paginateForward), String(updateFilter)],
      searchSaga
    ),
    takeEvery(suggestTaxonomy.REQUESTED, suggestSaga),
    takeEvery(search.REJECTED, handleSearchRejected)
  ]);
}
